/**
 * Reels voice-over hub — private admin utility.
 * Preview + download the 9 generated FR voice-overs (3 angles × 3 durations).
 * Not linked in any nav; accessible via /admin/reels only.
 */
import React, { useMemo } from "react";
import { Download, Play } from "lucide-react";

const ANGLES = [
  {
    id: "A",
    name: "Vulgarisation",
    kicker: "Grand public curieux",
    tone: "text-emerald-400 border-emerald-500/30 bg-emerald-500/5",
    scripts: {
      "10s": "Chaque fois que vous utilisez l'intelligence artificielle, quelque part dans le monde, un bâtiment s'allume. Des milliers de processeurs, refroidis en permanence. C'est l'infrastructure qui rend tout cela possible.",
      "15s": "Chaque fois que vous utilisez l'intelligence artificielle, un bâtiment s'allume à l'autre bout du monde. Des kilomètres de câbles, des salles refroidies à vingt-et-un degrés, des milliers de processeurs qui calculent en silence. C'est l'infrastructure invisible sur laquelle repose l'ensemble de l'économie numérique.",
      "25s": "Chaque fois que vous posez une question à l'intelligence artificielle, un bâtiment s'allume à des milliers de kilomètres. Des kilomètres de câbles, des salles refroidies à vingt-et-un degrés, des milliers de processeurs qui calculent en silence. On les appelle les data centers. Ils font tourner la moitié du monde numérique. Et pourtant, presque personne ne les a jamais vus. C'est une histoire qui mérite d'être racontée.",
    },
  },
  {
    id: "B",
    name: "Chiffre choc",
    kicker: "Hook fort · partageable",
    tone: "text-amber-400 border-amber-500/30 bg-amber-500/5",
    scripts: {
      "10s": "Quatre cent cinquante térawattheures. C'est la consommation électrique des data centers en 2024. L'équivalent d'un pays comme la France, entière.",
      "15s": "Quatre cent cinquante térawattheures. C'est la consommation électrique mondiale des data centers en 2024. L'équivalent d'un pays comme la France, entière. Et cette consommation va être multipliée par dix d'ici 2030. Sous l'effet, uniquement, de l'intelligence artificielle.",
      "25s": "Quatre cent cinquante térawattheures. C'est la consommation électrique mondiale des data centers en 2024. L'équivalent d'un pays comme la France, entière. Cette consommation va être multipliée par dix d'ici 2030, sous l'effet unique de l'intelligence artificielle. Chaque requête, chaque image générée, chaque modèle entraîné, ajoute une charge supplémentaire. Le vrai goulot d'étranglement de la prochaine décennie ne sera pas le silicium. Ce sera l'énergie.",
    },
  },
  {
    id: "C",
    name: "Nouvelle classe d'actif",
    kicker: "Investisseurs qualifiés",
    tone: "text-sky-400 border-sky-500/30 bg-sky-500/5",
    scripts: {
      "10s": "Ce que Blackrock, Google et Microsoft construisent en secret depuis trois ans. Des campus de dix milliards de dollars. Pour héberger une seule chose : l'intelligence artificielle.",
      "15s": "Ce que Blackrock, Google et Microsoft construisent en secret depuis trois ans. Des campus industriels à dix milliards de dollars la pièce. Pour héberger une seule et même chose : l'intelligence artificielle. Une nouvelle catégorie d'actif est en train de naître, sous nos yeux.",
      "25s": "Ce que Blackrock, Google et Microsoft construisent en secret depuis trois ans. Des campus industriels à dix milliards de dollars la pièce, alimentés par des barrages hydroélectriques entiers. Pour héberger une seule et même chose : l'intelligence artificielle. Une nouvelle catégorie d'actif industriel est en train de naître, sous nos yeux. Longtemps réservée aux fonds institutionnels, elle commence tout juste à s'ouvrir. Il est temps de comprendre pourquoi.",
    },
  },
  {
    id: "D",
    name: "L'énergie, la vraie limite",
    kicker: "NOUVEAU · 10s · vidéo à produire via Video Director",
    tone: "text-orange-400 border-orange-500/30 bg-orange-500/5",
    scripts: {
      "10s": "Un rack de serveurs d'intelligence artificielle consomme jusqu'à cent kilowatts. Dix fois plus qu'avant. À l'échelle d'un site, c'est une ville entière. La vraie limite de l'IA ne sera pas le silicium. Ce sera l'énergie.",
    },
  },
  {
    id: "E",
    name: "La géographie cachée",
    kicker: "NOUVEAU · 10s · vidéo à produire via Video Director",
    tone: "text-indigo-400 border-indigo-500/30 bg-indigo-500/5",
    scripts: {
      "10s": "Les data centers se cachent. Fjords de Norvège, déserts d'Arizona, barrages du Québec. Là où l'énergie est abondante, stable et décarbonée. Une géographie invisible est en train de se redessiner.",
    },
  },
  {
    id: "F",
    name: "L'eau et le refroidissement",
    kicker: "NOUVEAU · 10s · vidéo à produire via Video Director",
    tone: "text-teal-400 border-teal-500/30 bg-teal-500/5",
    scripts: {
      "10s": "Pour empêcher les processeurs de fondre, certains data centers consomment des millions de litres d'eau. Chaque jour. Le refroidissement est devenu l'enjeu silencieux de l'intelligence artificielle.",
    },
  },
  {
    id: "G",
    name: "Location GPU — landing froide",
    kicker: "NOUVEAU · 20s · micro-vidéo 15-25s pour landing /gpu",
    tone: "text-lime-400 border-lime-500/30 bg-lime-500/5",
    scripts: {
      "20s": "Loue de la puissance de calcul, et reçois des revenus. Tu choisis la puissance que tu mets à disposition. On organise la location. Tu reçois ta part des revenus générés. Simple, transparent, sans engagement. Estime tes revenus potentiels, dès maintenant.",
    },
  },
];

const DURATIONS = ["10s", "15s", "25s"];
const FILE_BASE = { A: "vulgarisation", B: "chiffrechoc", C: "nouvelleclasse", D: "energie", E: "geographie", F: "eau", G: "locationgpu" };
const durationsFor = (id) => (["A", "B", "C"].includes(id) ? DURATIONS : (id === "G" ? ["20s"] : ["10s"]));
const nameFor = (angle, dur) => `${angle}-${FILE_BASE[angle]}-${dur}.mp3`;

export default function AdminReels() {
  const CAPTIONS = useMemo(() => ({
    A: `L'infrastructure invisible de l'IA.\n\nDes bâtiments industriels que personne ne voit et qui font tourner la moitié du monde numérique.\n\nLisez le dossier complet en bio.\n\n#IntelligenceArtificielle #DataCenter #Innovation #Technologie #Futur`,
    B: `450 térawattheures. La consommation électrique des data centers en 2024.\n\nCe chiffre va être multiplié par dix d'ici 2030. Sous l'effet unique de l'IA.\n\nLien en bio pour comprendre pourquoi.\n\n#IntelligenceArtificielle #Energie #DataCenter #Transition #Innovation`,
    C: `Ce que Blackrock, Google et Microsoft construisent en secret depuis 3 ans.\n\nUne nouvelle catégorie d'actif industriel est en train de naître.\n\nLisez l'enquête en bio.\n\n#Investissement #DataCenter #IA #NouvelleClassedActif #Enquete`,
  }), []);

  const copy = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="min-h-screen bg-background py-10 md:py-16" data-testid="admin-reels">
      <div className="max-w-5xl mx-auto px-6">
        <p className="overline text-muted-foreground">Instagram Reels · Kit voix-off</p>
        <h1 className="mt-1 font-display text-4xl md:text-5xl tracking-tighter">Kit Reel — 3 angles × 3 durées</h1>
        <p className="mt-3 text-[13px] text-muted-foreground max-w-3xl">
          9 voix-off en français prêtes à monter. Voix OpenAI <span className="font-mono text-[12px]">onyx</span> (masculin grave, tts-1-hd, vitesse 0.95). Télécharge le MP3, importe-le dans CapCut, ajoute la vidéo <span className="font-mono text-[12px]">/videos/insight-hero-v3.mp4</span> en fond, puis exporte en 9:16.
        </p>

        <div className="mt-10 space-y-10">
          {ANGLES.map(a => (
            <section key={a.id} className={`rounded-2xl border ${a.tone} p-6 md:p-8`} data-testid={`reels-angle-${a.id}`}>
              <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
                <div>
                  <p className="overline">Angle {a.id} · {a.kicker}</p>
                  <h2 className="mt-1 font-display text-2xl tracking-tight text-foreground">{a.name}</h2>
                </div>
                <button
                  onClick={() => copy(CAPTIONS[a.id])}
                  className="text-[10px] tracking-widest uppercase border border-border px-3 h-8 rounded-full text-muted-foreground hover:bg-secondary"
                  data-testid={`reels-copy-caption-${a.id}`}
                >Copy caption + hashtags</button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {durationsFor(a.id).map(d => {
                  const url = `/audio/reels/${nameFor(a.id, d)}`;
                  return (
                    <div key={d} className="rounded-xl border border-border bg-card p-4 flex flex-col gap-3">
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-[11px] tracking-wider text-muted-foreground">DURATION</span>
                        <span className="font-display text-lg text-foreground">{d}</span>
                      </div>
                      <audio controls preload="none" src={url} className="w-full h-10" data-testid={`reels-audio-${a.id}-${d}`}>
                        <track kind="captions" />
                      </audio>
                      <a
                        href={url}
                        download
                        className="inline-flex items-center justify-center gap-2 h-9 px-3 rounded-full bg-foreground text-background text-[10px] tracking-widest uppercase hover:opacity-90"
                        data-testid={`reels-download-${a.id}-${d}`}
                      >
                        <Download className="w-3.5 h-3.5" /> Download MP3
                      </a>
                      <details className="text-[12px] text-muted-foreground leading-relaxed">
                        <summary className="cursor-pointer overline">Script</summary>
                        <p className="mt-2 text-foreground/80 italic">{a.scripts[d]}</p>
                      </details>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        <section className="mt-14 rounded-2xl border border-dashed border-border p-6" data-testid="reels-howto">
          <p className="overline text-muted-foreground">Comment monter le Reel en 5 min (CapCut mobile)</p>
          <ol className="mt-4 space-y-3 text-[13px] leading-relaxed text-foreground/90 list-decimal pl-5">
            <li>Ouvre <strong>CapCut</strong> → Nouveau projet → format <span className="font-mono">9:16</span></li>
            <li>Importe la vidéo <a href="/videos/insight-hero-v3.mp4" className="underline" target="_blank" rel="noreferrer">insight-hero-v3.mp4</a> et coupe-la à la durée de la voix-off (10 / 15 / 25 sec)</li>
            <li>Importe le MP3 → l'ajoute sur la piste audio → mets la musique de fond à <span className="font-mono">-18 dB</span> et la voix à <span className="font-mono">0 dB</span></li>
            <li>Ajoute 2-3 textes overlay (typographie sans-serif blanche, gras, animation "fondu") synchronisés avec les mots forts de la voix-off</li>
            <li>Termine sur un plan noir 1 sec avec le texte <em>"Lien en bio"</em> et flèche vers le haut</li>
            <li>Exporte en 1080×1920 · 60 fps · qualité "supérieure"</li>
          </ol>
          <p className="mt-4 text-[11px] text-muted-foreground">
            <strong>Musique libre de droit</strong> recommandée : cherche sur Epidemic Sound ou dans la bibliothèque Instagram — mots-clés <span className="font-mono">cinematic ambient</span>, <span className="font-mono">documentary underscore</span>, <span className="font-mono">deep pulse</span>.
          </p>
        </section>
      </div>
    </div>
  );
}
